import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import API from "../APIs/API";
import Sidebar from "../components/Sidebar";
import "../styles/profile.scss";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useMe } from "../hooks/useMe";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const limitedFilter = createFilterOptions({ limit: 50 });

// Reusable freeSolo Autocomplete that also allows manual entry
const DropdownField = ({ label, name, value, onChange, options = [], required = false }) => (
  <div className="form-outline" style={{ marginBottom: 12 }}>
    <label className="form-label" htmlFor={name}>
      <b>{label}</b>
    </label>
    <Autocomplete
      freeSolo
      filterOptions={limitedFilter}
      options={options}
      value={value ?? ""}
      onChange={(_, newValue) => {
        onChange({ target: { name, value: newValue ?? "" } });
      }}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input") {
          onChange({ target: { name, value: newValue ?? "" } });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          variant="outlined"
          name={name}
          id={name}
          required={required}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff",
              fontSize: 14,
            },
          }}
        />
      )}
      sx={{ mt: 0.5 }}
    />
  </div>
);

// Plain text input (for non-dropdown fields)
const PlainField = ({ label, name, value, onChange, required = false, disabled = false, type = "text" }) => (
  <div className="form-outline" style={{ marginBottom: 12 }}>
    <label className="form-label" htmlFor={name}>
      <b>{label}</b>
    </label>
    <br />
    <input
      className="form-control"
      type={type}
      name={name}
      id={name}
      value={value ?? ""}
      onChange={onChange}
      required={required}
      disabled={disabled}
    />
  </div>
);

const EmployeeAdd = ({ type }) => {
  const navigate = useNavigate();
  const { admin_location_id } = useParams();
  const pageUserType = type === "Non-Teacher" ? "non-teachers" : "teachers";
  const backUrl = `/${pageUserType}/${admin_location_id}`;

  const { user: loggedUser } = useMe();

  const initialEmployee = {
    employee_no: "",
    admin_location_id: admin_location_id,
    name_report: "",
    position_no: "",
    occup_pos_title: "",
    award: "",
    award_desc: "",
    classification: "",
    class_desc: "",
    step_no: "",
    occup_type: "",
    gender: "M",
    first_commence: "",
    account_no: "",
    account_no_desc: "",
    emp_status: "",
    paypoint: "",
    paypoint_desc: "",
    date_of_birth: "",
    occup_pos_cat: "",
  };

  const [employee, setEmployee] = useState(initialEmployee);
  const [adminLocation, setAdminLocation] = useState({
    id: 0,
    admin_location: "",
    admin_desc: "",
    user_type: "",
  });

  useEffect(() => {
    new API().getAdminLocationById(admin_location_id).then((data) => {
      setAdminLocation({ ...data["data"] });
    });
  }, [admin_location_id]);

  // Load dropdown options from DB (same endpoint as StaffView)
  const { data: fieldOptions = {} } = useQuery(["staff_field_options"], {
    queryFn: async () => {
      const result = await new API().getStaffFieldOptions();
      return result?.data ?? {};
    },
    staleTime: 5 * 60 * 1000,
  });

  const handleChange = (e) => {
    setEmployee((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const createEmployee = useMutation((body) => new API().addEmployee(body), {
    onSuccess: (data) => {
      if (data) {
        toast.success("Employee created successfully!");
        navigate(backUrl);
      } else {
        toast.error("Employee creation failed!");
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createEmployee.mutate(employee);
  };

  const opts = fieldOptions; // shorthand

  return (
    <div className="wrapper">
      <div className="sidebar">
        <Sidebar />
      </div>
      <div className="content">
        <div className="profile">
          <div className="profileContainer">
            <div className="profileSection">
              <Link to={`/${pageUserType}/${admin_location_id}`} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br />
              <br />

              <form onSubmit={handleSubmit}>
                <div className="container">
                  <div className="row">
                    <div className="col col-sm">
                      <h1>Create New Employee</h1>
                    </div>
                    <div className="col col-sm">
                      <button
                        type="submit"
                        className="saveBtn btn btn-primary btn-block mb-3"
                        disabled={createEmployee.isLoading}
                      >
                        {createEmployee.isLoading ? (
                          <>
                            <CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} />
                            Creating…
                          </>
                        ) : (
                          "Create"
                        )}
                      </button>
                    </div>
                  </div>
                  <hr />

                  <div className="row">
                    {/* ── Column 1 ── */}
                    <div className="col col-sm">
                      <PlainField
                        label="User type"
                        name="user_type"
                        value={adminLocation?.user_type}
                        onChange={handleChange}
                        disabled
                      />
                      <PlainField
                        label="Employee no"
                        name="employee_no"
                        value={employee?.employee_no}
                        onChange={handleChange}
                        required
                      />
                      <PlainField
                        label="Name report"
                        name="name_report"
                        value={employee?.name_report}
                        onChange={handleChange}
                        required
                      />
                      <PlainField
                        label="Admin location"
                        name="admin_location"
                        value={adminLocation?.admin_location}
                        onChange={handleChange}
                        disabled
                      />
                      <PlainField
                        label="Admin desc"
                        name="admin_desc"
                        value={adminLocation?.admin_desc}
                        onChange={handleChange}
                        disabled
                      />
                      <DropdownField
                        label="Position no"
                        name="position_no"
                        value={employee?.position_no}
                        onChange={handleChange}
                        options={opts.position_no ?? []}
                        required
                      />
                      <DropdownField
                        label="Occup pos title"
                        name="occup_pos_title"
                        value={employee?.occup_pos_title}
                        onChange={handleChange}
                        options={opts.occup_pos_title ?? []}
                        required
                      />
                      <DropdownField
                        label="Award"
                        name="award"
                        value={employee?.award}
                        onChange={handleChange}
                        options={opts.award ?? []}
                        required
                      />
                    </div>

                    {/* ── Column 2 ── */}
                    <div className="col col-sm">
                      <DropdownField
                        label="Award desc"
                        name="award_desc"
                        value={employee?.award_desc}
                        onChange={handleChange}
                        options={opts.award_desc ?? []}
                        required
                      />
                      <DropdownField
                        label="Classification"
                        name="classification"
                        value={employee?.classification}
                        onChange={handleChange}
                        options={opts.classification ?? []}
                        required
                      />
                      <DropdownField
                        label="Class desc"
                        name="class_desc"
                        value={employee?.class_desc}
                        onChange={handleChange}
                        options={opts.class_desc ?? []}
                        required
                      />
                      <PlainField
                        label="Step no"
                        name="step_no"
                        value={employee?.step_no}
                        onChange={handleChange}
                        required
                      />
                      <DropdownField
                        label="Occup type"
                        name="occup_type"
                        value={employee?.occup_type}
                        onChange={handleChange}
                        options={opts.occup_type ?? []}
                        required
                      />
                      {/* Gender stays as a native select — only 2 fixed values */}
                      <div className="form-outline" style={{ marginBottom: 12 }}>
                        <label className="form-label" htmlFor="gender">
                          <b>Gender</b>
                        </label>
                        <br />
                        <select
                          className="form-select"
                          name="gender"
                          id="gender"
                          value={employee?.gender}
                          onChange={handleChange}
                        >
                          <option value="M">Male</option>
                          <option value="F">Female</option>
                        </select>
                      </div>
                      {/* Date picker for First commence */}
                      <div className="form-outline" style={{ marginBottom: 12 }}>
                        <label className="form-label" htmlFor="first_commence">
                          <b>First commence</b>
                        </label>
                        <br />
                        <DatePicker
                          value={employee.first_commence ? dayjs(employee.first_commence) : null}
                          onChange={(newValue) =>
                            setEmployee((prev) => ({
                              ...prev,
                              first_commence: newValue ? newValue.format("YYYY-MM-DD") : "",
                            }))
                          }
                          views={["year", "month", "day"]}
                          inputFormat="YYYY-MM-DD"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              fullWidth
                              id="first_commence"
                              sx={{
                                mt: 0.5,
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#fff",
                                  fontSize: 14,
                                },
                              }}
                            />
                          )}
                        />
                      </div>
                      <DropdownField
                        label="Account no"
                        name="account_no"
                        value={employee?.account_no}
                        onChange={handleChange}
                        options={opts.account_no ?? []}
                        required
                      />
                    </div>

                    {/* ── Column 3 ── */}
                    <div className="col col-sm">
                      <DropdownField
                        label="Account no desc"
                        name="account_no_desc"
                        value={employee?.account_no_desc}
                        onChange={handleChange}
                        options={opts.account_no_desc ?? []}
                        required
                      />
                      <DropdownField
                        label="Emp status"
                        name="emp_status"
                        value={employee?.emp_status}
                        onChange={handleChange}
                        options={opts.emp_status ?? []}
                        required
                      />
                      <DropdownField
                        label="Paypoint"
                        name="paypoint"
                        value={employee?.paypoint}
                        onChange={handleChange}
                        options={opts.paypoint ?? []}
                        required
                      />
                      <DropdownField
                        label="Paypoint desc"
                        name="paypoint_desc"
                        value={employee?.paypoint_desc}
                        onChange={handleChange}
                        options={opts.paypoint_desc ?? []}
                        required
                      />
                      {/* Date picker for Date of birth */}
                      <div className="form-outline" style={{ marginBottom: 12 }}>
                        <label className="form-label" htmlFor="date_of_birth">
                          <b>Date of birth</b>
                        </label>
                        <br />
                        <DatePicker
                          value={employee.date_of_birth ? dayjs(employee.date_of_birth) : null}
                          onChange={(newValue) =>
                            setEmployee((prev) => ({
                              ...prev,
                              date_of_birth: newValue ? newValue.format("YYYY-MM-DD") : "",
                            }))
                          }
                          views={["year", "month", "day"]}
                          inputFormat="YYYY-MM-DD"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              fullWidth
                              id="date_of_birth"
                              sx={{
                                mt: 0.5,
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#fff",
                                  fontSize: 14,
                                },
                              }}
                            />
                          )}
                        />
                      </div>
                      <DropdownField
                        label="Occup pos cat"
                        name="occup_pos_cat"
                        value={employee?.occup_pos_cat}
                        onChange={handleChange}
                        options={opts.occup_pos_cat ?? []}
                        required
                      />
                      <PlainField
                        label="Created by"
                        name="created_by"
                        value={loggedUser?.email}
                        onChange={handleChange}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAdd;
